import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiServiceService } from '../../services/api-service.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiServiceService);
  
  // Static cache for logo - store Blob to allow per-instance URL creation (avoids memory leak)
  private static cachedLogoBlob: Blob | null = null;
  private static logoLoading: boolean = false;
  private static logoLoadCallbacks: Array<(blob: Blob | null) => void> = [];
  
  // Static cache for user details to prevent repeated API calls
  private static cachedUserDetails: any = null;
  private static userDetailsLoading: boolean = false;
  private static userDetailsLoadCallbacks: Array<(details: any) => void> = [];
  private static readonly USER_DETAILS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly USER_DETAILS_TIMESTAMP_KEY = 'loggedInUserDetailsTimestamp';
  private static userDetailsCacheTimestamp: number = 0; // In-memory fallback when localStorage unavailable
  
  // Instance state
  private destroyed = false;
  private logoCallback: ((blob: Blob | null) => void) | null = null;
  private userDetailsCallback: ((details: any) => void) | null = null;
  private subscriptions: Subscription[] = [];
  private isLogoInitiator = false;
  private isUserDetailsInitiator = false;
  
  // Properties referenced in the template
  logo: string | null = null;
  private objectUrl: string | null = null;
  homeUrl: string = '/';
  userDetials: any = null; // Keeping the name as used in the template
  userRole: string = '';
  showDropdown: boolean = false;

  ngOnInit(): void {
    this.loadLogo();
    this.loadUserDetails();
    this.setHomeUrl();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    
    // Remove any pending callbacks to prevent execution after destroy
    if (this.logoCallback) {
      const idx = HeaderComponent.logoLoadCallbacks.indexOf(this.logoCallback);
      if (idx > -1) {
        HeaderComponent.logoLoadCallbacks.splice(idx, 1);
      }
      this.logoCallback = null;
    }
    
    if (this.userDetailsCallback) {
      const idx = HeaderComponent.userDetailsLoadCallbacks.indexOf(this.userDetailsCallback);
      if (idx > -1) {
        HeaderComponent.userDetailsLoadCallbacks.splice(idx, 1);
      }
      this.userDetailsCallback = null;
    }
    
    // If this instance initiated an API call that's still in-flight, reset loading state
    // and notify waiting callbacks so they don't hang forever
    if (this.isLogoInitiator && HeaderComponent.logoLoading) {
      HeaderComponent.logoLoading = false;
      const callbacks = HeaderComponent.logoLoadCallbacks;
      HeaderComponent.logoLoadCallbacks = [];
      callbacks.forEach(cb => cb(null)); // Notify with null so they can retry
    }
    
    if (this.isUserDetailsInitiator && HeaderComponent.userDetailsLoading) {
      HeaderComponent.userDetailsLoading = false;
      const callbacks = HeaderComponent.userDetailsLoadCallbacks;
      HeaderComponent.userDetailsLoadCallbacks = [];
      callbacks.forEach(cb => cb(null)); // Notify with null so they can use localStorage fallback
    }
    
    // Unsubscribe from any pending API calls
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    
    // Clean up blob URL to prevent memory leaks - each instance owns its URL
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  private setHomeUrl(): void {
    // SM-3009: Home URL comes from environment.homePageUrl (the Angular UI URL),
    // e.g. https://ui-sm.exostardev.com/obmui. Trailing slashes trimmed.
    const configuredHomeUrl = (environment.homePageUrl || '').replace(/\/+$/, '');
    if (configuredHomeUrl) {
      this.homeUrl = configuredHomeUrl;
      return;
    }

    // Resolve base href against current URL to handle relative paths correctly
    // e.g., './' on '/formdesigner/' resolves to '/formdesigner/'
    const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
    try {
      const resolvedUrl = new URL(baseHref, window.location.href);
      this.homeUrl = resolvedUrl.pathname.replace(/\/+$/, '') || '/';
    } catch {
      // Fallback if URL parsing fails - normalize '.' to '/' for safety
      const fallback = baseHref.replace(/\/+$/, '') || '/';
      this.homeUrl = fallback === '.' ? '/' : fallback;
    }
  }

  private loadLogo(): void {
    // Use cached blob if available - create a new URL for this instance
    if (HeaderComponent.cachedLogoBlob) {
      // Revoke any existing URL before creating a new one to prevent leaks
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
      }
      this.objectUrl = URL.createObjectURL(HeaderComponent.cachedLogoBlob);
      this.logo = this.objectUrl;
      return;
    }
    
    // If another instance is loading, register callback to be notified when done
    if (HeaderComponent.logoLoading) {
      // Create instance-owned callback that guards against destroyed state
      this.logoCallback = (blob: Blob | null) => {
        if (this.destroyed) return;
        this.logoCallback = null; // Clear callback reference since it's been executed
        if (blob) {
          // Revoke any existing URL before creating a new one
          if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
          }
          this.objectUrl = URL.createObjectURL(blob);
          this.logo = this.objectUrl;
        } else {
          // Initiator was destroyed or request failed - retry since logoLoading is now false
          this.loadLogo();
        }
      };
      HeaderComponent.logoLoadCallbacks.push(this.logoCallback);
      return;
    }
    
    HeaderComponent.logoLoading = true;
    this.isLogoInitiator = true;
    const logoSub = this.apiService.GetLogo().subscribe({
      next: (imageBlob: Blob) => {
        // Cache the Blob (not the URL) so each instance can create its own revocable URL
        HeaderComponent.cachedLogoBlob = imageBlob;
        
        if (!this.destroyed) {
          // Revoke any existing URL before creating a new one (defensive)
          if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
          }
          this.objectUrl = URL.createObjectURL(imageBlob);
          this.logo = this.objectUrl;
        }
        
        HeaderComponent.logoLoading = false;
        this.isLogoInitiator = false;
        
        // Notify all waiting instances - capture and clear first to avoid issues
        const callbacks = HeaderComponent.logoLoadCallbacks;
        HeaderComponent.logoLoadCallbacks = [];
        callbacks.forEach(cb => cb(imageBlob));
      },
      error: (error) => {
        console.error('Error loading logo:', error);
        if (!this.destroyed) {
          this.logo = null;
        }
        HeaderComponent.logoLoading = false;
        this.isLogoInitiator = false;
        
        // Notify waiting instances of failure - capture and clear first
        const callbacks = HeaderComponent.logoLoadCallbacks;
        HeaderComponent.logoLoadCallbacks = [];
        callbacks.forEach(cb => cb(null));
      }
    });
    this.subscriptions.push(logoSub);
  }

  private loadUserDetails(): void {
    const now = Date.now();
    
    // Helper to safely read localStorage (can throw in Safari private mode)
    const safeGetItem = (key: string): string | null => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    };
    
    // Check static cache first (valid within current session)
    if (HeaderComponent.cachedUserDetails) {
      // Use localStorage timestamp if available, fall back to in-memory timestamp when localStorage unavailable
      const storedTimestamp = parseInt(safeGetItem(HeaderComponent.USER_DETAILS_TIMESTAMP_KEY) || '0', 10) || HeaderComponent.userDetailsCacheTimestamp;
      if ((now - storedTimestamp) < HeaderComponent.USER_DETAILS_CACHE_TTL) {
        this.userDetials = HeaderComponent.cachedUserDetails;
        this.setUserRole();
        return;
      }
    }
    
    // Check localStorage for cached user details with timestamp
    const storedUserDetails = safeGetItem('loggedInUserDetails');
    const storedTimestamp = parseInt(safeGetItem(HeaderComponent.USER_DETAILS_TIMESTAMP_KEY) || '0', 10);
    
    if (storedUserDetails && storedTimestamp && (now - storedTimestamp) < HeaderComponent.USER_DETAILS_CACHE_TTL) {
      try {
        this.userDetials = JSON.parse(storedUserDetails);
        HeaderComponent.cachedUserDetails = this.userDetials;
        this.setUserRole();
        return;
      } catch {
        // Clear invalid localStorage data (wrapped in try/catch as localStorage can throw)
        try {
          localStorage.removeItem('loggedInUserDetails');
          localStorage.removeItem(HeaderComponent.USER_DETAILS_TIMESTAMP_KEY);
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }
      }
    }
    
    // If another instance is loading, register callback but use localStorage as immediate fallback
    if (HeaderComponent.userDetailsLoading) {
      // Use localStorage data immediately while waiting for the API response
      if (storedUserDetails) {
        try {
          this.userDetials = JSON.parse(storedUserDetails);
          this.setUserRole();
        } catch {
          // Clear invalid localStorage data so future instances don't repeatedly fail
          try {
            localStorage.removeItem('loggedInUserDetails');
            localStorage.removeItem(HeaderComponent.USER_DETAILS_TIMESTAMP_KEY);
          } catch (e) {
            console.warn('Failed to clear invalid localStorage:', e);
          }
        }
      }
      
      // Create instance-owned callback that guards against destroyed state
      this.userDetailsCallback = (details: any) => {
        if (this.destroyed) return;
        this.userDetailsCallback = null; // Clear callback reference since it's been executed
        if (details) {
          this.userDetials = details;
          this.setUserRole();
        } else if (!this.userDetials) {
          // Initiator was destroyed or request failed, and no localStorage fallback - retry
          this.loadUserDetails();
        }
      };
      HeaderComponent.userDetailsLoadCallbacks.push(this.userDetailsCallback);
      return;
    }
    
    // Use localStorage data immediately while fetching fresh data in background
    if (storedUserDetails) {
      try {
        this.userDetials = JSON.parse(storedUserDetails);
        HeaderComponent.cachedUserDetails = this.userDetials;
        this.setUserRole();
      } catch {
        // Clear invalid localStorage data so future instances don't repeatedly fail
        try {
          localStorage.removeItem('loggedInUserDetails');
          localStorage.removeItem(HeaderComponent.USER_DETAILS_TIMESTAMP_KEY);
        } catch (e) {
          console.warn('Failed to clear invalid localStorage:', e);
        }
      }
    }
    
    HeaderComponent.userDetailsLoading = true;
    this.isUserDetailsInitiator = true;
    const userDetailsSub = this.apiService.GetUserLoginDetails().subscribe({
      next: (userDetails: any) => {
        HeaderComponent.cachedUserDetails = userDetails;
        HeaderComponent.userDetailsLoading = false;
        this.isUserDetailsInitiator = false;
        
        // Update in-memory timestamp (always works) and persist to localStorage (may throw)
        const fetchTime = Date.now();
        HeaderComponent.userDetailsCacheTimestamp = fetchTime;
        try {
          localStorage.setItem('loggedInUserDetails', JSON.stringify(userDetails));
          localStorage.setItem(HeaderComponent.USER_DETAILS_TIMESTAMP_KEY, fetchTime.toString());
        } catch (e) {
          console.warn('Failed to persist user details to localStorage:', e);
        }
        
        if (!this.destroyed) {
          this.userDetials = userDetails;
          this.setUserRole();
        }
        
        // Always notify waiting instances - capture and clear callbacks first to avoid issues
        const callbacks = HeaderComponent.userDetailsLoadCallbacks;
        HeaderComponent.userDetailsLoadCallbacks = [];
        callbacks.forEach(cb => cb(userDetails));
      },
      error: (error) => {
        console.error('Error loading user details:', error);
        HeaderComponent.userDetailsLoading = false;
        this.isUserDetailsInitiator = false;
        
        // Notify waiting instances of failure (they may have localStorage fallback)
        const callbacks = HeaderComponent.userDetailsLoadCallbacks;
        HeaderComponent.userDetailsLoadCallbacks = [];
        callbacks.forEach(cb => cb(null));
      }
    });
    this.subscriptions.push(userDetailsSub);
  }

  private setUserRole(): void {
    // Use userRoleNames from API which contains actual display names (e.g., "Super User")
    // instead of hardcoded role map which shows "Dynamic" for dynamic roles
    const roleName = this.userDetials?.userRoleNames?.[0] || this.userDetials?.UserRoleNames?.[0];
    if (roleName) {
      this.userRole = roleName;
      return;
    }
    
    // Fallback to role map lookup if userRoleNames not available
    if (!this.userDetials?.userRole && !this.userDetials?.UserRole) return;
    
    const userRoleValue = this.userDetials.userRole?.[0] ?? this.userDetials.UserRole?.[0];
    const roleMap: { [key: number]: string } = {
      0: 'SupplierSubmitter',
      1: 'SupplierAdministrator',
      2: 'BuyerUser',
      3: 'BuyerFSS',
      4: 'BuyerTechnicalAdministrator',
      5: 'BuyerFunctionAdministrator',
      6: 'SupportCoordinator',
      7: 'SupportAdministrator'
    };
    this.userRole = roleMap[userRoleValue] || '';
  }

  // Methods referenced in the template
  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  logout(): void {
    // Clear localStorage and redirect to home (wrapped in try/catch as localStorage can throw)
    try {
      localStorage.clear();
    } catch (e) {
      console.warn('Failed to clear localStorage on logout:', e);
    }
    window.location.href = this.homeUrl || '/';
  }

  confirmSignout(): void {
    // Implement modal display logic
    document.getElementById('signoutConfirmModal')?.classList.add('show');
  }
}
